import React, { useEffect, useRef, useState } from 'react'
import {
  AlignItems,
  Badge,
  Body3,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Heading6,
  Image,
  JustifyContent,
  Text,
  TextAlign,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import Plate1 from './images/plate_1.png'
import Plate2 from './images/plate_2.png'
import Plate3 from './images/plate_3.png'
import Plate4 from './images/plate_4.png'
import Plate5 from './images/plate_5.png'
import Plate6 from './images/plate_6.png'
import Plate7 from './images/plate_7.png'
import Plate8 from './images/plate_8.png'
import { CSSObject } from '@emotion/react'
import { queueTrackingEvent } from '@library/tracking-integration'

type ScrollableBasketEducationDismissedEvent = {
  action: 'scrollable_basket_education_dismissed'
}

const RecipeCountBadge = withCitrusTheme(Badge, (theme) => ({
  backgroundColor: theme.color(Color.Secondary_600),
  color: theme.color(Color.White),
  height: theme.spacing(6),
  width: theme.spacing(6),
  display: Display.Flex,
  justifyContent: JustifyContent.Center,
  '& span': {
    fontSize: '0.6rem',
    lineHeight: '0.9rem',
    fontWeight: 700,
  },
}))

const Container = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.White),
  borderRadius: theme.spacing(1),
  width: '100%',
  maxWidth: '380px',
  position: 'absolute',
  left: '50%',
  ...(theme.responsiveStyle(['80px', '0'], ['bottom']) as CSSObject),
  transform: 'translate(-50%, -100px)',
  zIndex: theme.zIndex(ZIndex.Modal),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '0',
    left: '50%',
    borderTopLeftRadius: theme.spacing(10),
    width: theme.spacing(6),
    height: theme.spacing(6),
    transform: 'rotate(45deg) translateY(50%)',
    backgroundColor: theme.color(Color.White),
  },
}))

const images = [Plate1, Plate2, Plate3, Plate4, Plate5, Plate6, Plate7, Plate8]

export function Tooltip({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [items, setItems] = useState<number[]>([0])
  const firstItemRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)

  const addItem = () => {
    setItems((prev) => [...prev, prev.length])
  }

  useEffect(() => {
    const interval: ReturnType<typeof setInterval> = setInterval(() => {
      if (items.length >= 8) {
        clearInterval(interval)
        firstItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          inline: 'end',
        })
        return
      }
      addItem()
    }, 1000)

    return () => clearInterval(interval)
  }, [items.length])

  useEffect(() => {
    if (lastItemRef.current) {
      lastItemRef.current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'end',
      })
    }
  }, [items])

  const handleClose = () => {
    queueTrackingEvent<ScrollableBasketEducationDismissedEvent>({
      action: 'scrollable_basket_education_dismissed',
    })
    onClose()
  }

  if (!open) {
    return null
  }

  return (
    <Box data-testid="basketTooltip">
      <Box
        position={DisplayPosition.Absolute}
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        zIndex={ZIndex.Overlay}
        style={{ backgroundColor: 'rgba(33, 41, 48, 0.6)', transform: 'translateY(-100%)' }}
        onClick={handleClose}
        data-testid="basket-tooltip-overlay"
      />
      <Container>
        <Box
          width="100%"
          bg={Color.NeutralGrey_50}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          paddingV={4}
          position={DisplayPosition.Relative}
          borderRadius={1}
        >
          <Box
            position={DisplayPosition.Absolute}
            top="0"
            left="0"
            width="100%"
            height="100%"
            zIndex={ZIndex.Top}
          />
          <Box bg={Color.ColdGrey_50} maxWidth={['100%', '300px']} width="100%" paddingH={[5, 0]}>
            <Box
              display="flex"
              gap={3}
              paddingV={3}
              paddingH={3}
              style={{
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
              }}
            >
              {items.map((index) => (
                <Box
                  key={`plate-${index}`}
                  ref={index === 0 ? firstItemRef : index === items.length - 1 ? lastItemRef : null}
                  width="55px"
                  height="56px"
                  minWidth="55px"
                >
                  <Image src={images[index]} alt={`Plate image ${index}`} />
                </Box>
              ))}
            </Box>
            <Box paddingH={3} paddingBottom={3}>
              <Button width="100%" height="40px">
                <Box
                  width="100%"
                  display={Display.Flex}
                  alignItems={AlignItems.Center}
                  justifyContent={JustifyContent.SpaceBetween}
                >
                  <Box />
                  <Text fontWeight={FontWeight.Bold} size={1}>
                    View basket
                  </Text>
                  <RecipeCountBadge>{items.length}</RecipeCountBadge>
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          paddingH={5}
          paddingV={4}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={2}
        >
          <Heading6 textAlign={TextAlign.Center}>Still deciding?</Heading6>
          <Body3 color={Color.ColdGrey_900} textAlign={TextAlign.Center}>
            Drop as many recipes as you like in here then make your final selection before checkout.
          </Body3>
          <Button
            colorVariant={ButtonColorVariant.Tertiary}
            size={ButtonSize.Medium}
            onClick={handleClose}
            data-testid="keep-browsing-button"
          >
            Keep browsing
          </Button>
        </Box>
      </Container>
    </Box>
  )
}
